// src/components/admin/AdminNavbar.js
import React, { useState } from "react";
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  Avatar,
  Menu,
  MenuItem,
  Badge,
  Divider,
  ListItemIcon,
  InputAdornment,
} from "@mui/material";
import {
  Menu as MenuIcon,
  Notifications as NotificationsIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  Person as PersonIcon,
} from "@mui/icons-material";
import { themeColors } from "../../theme/colors";
import { useNavigate } from "react-router-dom";

const AdminNavbar = ({
  handleDrawerToggle,
  handleDesktopToggle,
  desktopOpen,
  drawerWidth,
  collapsedWidth,
}) => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [notificationAnchor, setNotificationAnchor] = useState(null);

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationOpen = (event) => {
    setNotificationAnchor(event.currentTarget);
  };

  const handleNotificationClose = () => {
    setNotificationAnchor(null);
  };

  const handleLogout = () => {
    // احذف بيانات الجلسة من التخزين المحلي ثم قم بتوجيه المتصفح إلى صفحة تسجيل الدخول
    localStorage.removeItem("user");
    localStorage.removeItem("authToken");
    // توجه إلى صفحة تسجيل الدخول عبر تغيير href — هذا سيؤدي إلى تحميل الصفحة الجديدة (ريفريش)
    window.location.href = "/login";
  };

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        width: {
          xs: "100%",
          md: desktopOpen
            ? `calc(100% - ${drawerWidth}px)`
            : `calc(100% - ${collapsedWidth}px)`,
        },
        mr: {
          xs: 0,
          md: desktopOpen ? `${drawerWidth}px` : `${collapsedWidth}px`,
        },
        backgroundColor: themeColors.background.paper,
        borderBottom: `1px solid ${themeColors.border.light}`,
        boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
        transition: "width 0.3s cubic-bezier(0.4, 0, 0.2, 1), margin 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        zIndex: (theme) => theme.zIndex.drawer - 1,
      }}
    >
      <Toolbar
        sx={{
          justifyContent: "space-between",
          minHeight: "64px",
          px: { xs: 2, sm: 3 },
        }}
      >
        {/* Left Section */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <IconButton
            color="inherit"
            aria-label="toggle drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{
              display: { md: "none" },
              color: themeColors.text.primary,
              "&:hover": {
                backgroundColor: themeColors.background.hover,
              },
            }}
          >
            <MenuIcon />
          </IconButton>

          <IconButton
            color="inherit"
            onClick={handleDesktopToggle}
            sx={{
              display: { xs: "none", md: "flex" },
              color: themeColors.text.primary,
              "&:hover": {
                backgroundColor: themeColors.background.hover,
              },
              transition: "all 0.2s",
            }}
          >
            {desktopOpen ? <MenuIcon /> : <MenuIcon />}
          </IconButton>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              px: { xs: 0, md: 1 },
            }}
          >
            <Typography
              variant="h6"
              noWrap
              component="div"
              sx={{
                fontWeight: 600,
                color: themeColors.text.primary,
                fontSize: { xs: "1rem", md: "1.25rem" },
              }}
            >
              لوحة تحكم الأدمن
            </Typography>
          </Box>
        </Box>

        {/* Right Section */}
        <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 0.5, sm: 1 } }}>

          {/* User Menu */}
          <IconButton
            onClick={handleMenuOpen}
            sx={{
              padding: 0.5,
              ml: 1,
              "&:hover": {
                backgroundColor: "transparent",
              },
            }}
          >
            <Avatar
              sx={{
                width: 38,
                height: 38,
                bgcolor: themeColors.primary.main,
                cursor: "pointer",
                transition: "all 0.2s",
                border: `2px solid ${themeColors.background.paper}`,
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                "&:hover": {
                  transform: "scale(1.05)",
                  boxShadow: "0 4px 12px rgba(255, 107, 53, 0.3)",
                },
              }}
            >
              A
            </Avatar>
          </IconButton>

          {/* User Dropdown Menu */}
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            transformOrigin={{ horizontal: "right", vertical: "top" }}
            anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
            PaperProps={{
              elevation: 8,
              sx: {
                mt: 1.5,
                minWidth: 240,
                borderRadius: 2,
                overflow: "visible",
                boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                "&:before": {
                  content: '""',
                  display: "block",
                  position: "absolute",
                  top: 0,
                  right: 14,
                  width: 10,
                  height: 10,
                  bgcolor: "background.paper",
                  transform: "translateY(-50%) rotate(45deg)",
                  zIndex: 0,
                },
                "& .MuiMenuItem-root": {
                  px: 2,
                  py: 1.5,
                  borderRadius: 1,
                  mx: 1,
                  fontSize: "0.9rem",
                  "&:hover": {
                    backgroundColor: themeColors.background.hover,
                  },
                },
              },
            }}
          >
            <MenuItem
              onClick={() => {
                handleMenuClose();
                handleLogout();
              }}
              sx={{
                color: themeColors.error,
                "&:hover": {
                  backgroundColor: "rgba(220, 53, 69, 0.08)",
                },
              }}
            >
              <ListItemIcon>
                <LogoutIcon fontSize="small" sx={{ color: themeColors.error }} />
              </ListItemIcon>
              تسجيل الخروج
            </MenuItem>
          </Menu>

          {/* Notifications Menu */}
          <Menu
            anchorEl={notificationAnchor}
            open={Boolean(notificationAnchor)}
            onClose={handleNotificationClose}
            transformOrigin={{ horizontal: "right", vertical: "top" }}
            anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
            PaperProps={{
              elevation: 8,
              sx: {
                mt: 1.5,
                minWidth: 340,
                maxWidth: 380,
                borderRadius: 2,
                overflow: "visible",
                boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                "&:before": {
                  content: '""',
                  display: "block",
                  position: "absolute",
                  top: 0,
                  right: 14,
                  width: 10,
                  height: 10,
                  bgcolor: "background.paper",
                  transform: "translateY(-50%) rotate(45deg)",
                  zIndex: 0,
                },
                "& .MuiMenuItem-root": {
                  px: 2,
                  py: 1.5,
                  whiteSpace: "normal",
                  "&:hover": {
                    backgroundColor: themeColors.background.hover,
                  },
                },
              },
            }}
          >
            <Box sx={{ px: 2, py: 1.5, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: themeColors.text.primary }}>
                الإشعارات
              </Typography>
              <Badge
                badgeContent={5}
                sx={{
                  "& .MuiBadge-badge": {
                    backgroundColor: themeColors.primary.main,
                    color: themeColors.text.white,
                  },
                }}
              />
            </Box>
            <Divider />
            <MenuItem onClick={handleNotificationClose}>
              <Box sx={{ width: "100%" }}>
                <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                  طلب اشتراك جديد
                </Typography>
                <Typography variant="caption" sx={{ color: themeColors.text.secondary }}>
                  منذ 10 دقائق
                </Typography>
              </Box>
            </MenuItem>
            <MenuItem onClick={handleNotificationClose}>
              <Box sx={{ width: "100%" }}>
                <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                  موزع جديد تم تسجيله
                </Typography>
                <Typography variant="caption" sx={{ color: themeColors.text.secondary }}>
                  منذ 30 دقيقة
                </Typography>
              </Box>
            </MenuItem>
            <MenuItem onClick={handleNotificationClose}>
              <Box sx={{ width: "100%" }}>
                <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                  تم إضافة نسخة جديدة
                </Typography>
                <Typography variant="caption" sx={{ color: themeColors.text.secondary }}>
                  منذ ساعة
                </Typography>
              </Box>
            </MenuItem>
            <Divider />
            <MenuItem
              onClick={handleNotificationClose}
              sx={{
                justifyContent: "center",
                color: themeColors.primary.main,
                fontWeight: 500,
                "&:hover": {
                  backgroundColor: themeColors.background.hover,
                },
              }}
            >
              عرض جميع الإشعارات
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default AdminNavbar;
